import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Field, FieldArray, useFormikContext } from 'formik';
import { connect } from 'react-redux';
import axios from 'axios';
import { getDistributorsOptions } from 'redux/distributors/selector';
import { getUserInstallersOptions } from 'redux/userInstallers/selector';
import { requests as distributorsRequest } from 'redux/distributors/reducer';
import { requests as userInstallersRequest } from 'redux/userInstallers/reducer';
import Button from '@material-ui/core/Button';
import DeleteIcon from '@material-ui/icons/Delete';
import PorletHeader, { PorletHeaderTitle } from 'components/porletHeader';
import FormHelperText from '@material-ui/core/FormHelperText';
import TextField from 'components/form/input';
import Autocomplete from 'components/form/autocomplete';
import DatePicker from 'components/datePicker';
import { createFilterOptions } from '@material-ui/lab/Autocomplete';
import FilmCatalogPreview from 'components/filmCatalogPreview';
import { getFasaraViewSheetImgSrc } from 'preview/sheetImageSrc';
import { fetchFasaraFilmByUuid } from '../../fetchFilmByUuid';
import './previewer.scss';

const API_URL = window.env.API_URL;
const ACCESS_TOKEN = window.env.ACCESS_TOKEN;

const CUSTOM_FAMILY_OPTIONS = [{ value: 'Custom', label: 'Custom' }];

const newSheet = {
  type: null,
  meters: null,
  distributor: null,
  custom: null,
  glassFinish: null,
  glassFinishLabel: null,
  film: null,
  filmLabel: null,
  filmUuid: null,
  filmData: null,
  family: null,
  familyLabel: null,
  run: null,
};

const newInstaller = {
  id: null,
  name: '',
};

export const initialValues = {
  installDate: null,
  installers: [{ ...newInstaller }],
  fasaraSheets: [],
  family: {},
};

const filter = createFilterOptions();

const filterOptions = (options, params) => {
  const filtered = filter(options, params);

  if (params.inputValue !== '') {
    filtered.push({
      value: {
        id: -1,
        name: params.inputValue,
      },
      label: (
        <>
          <FormattedMessage id="Agregar A:" /> "{params.inputValue}"
        </>
      ),
      useValue: false,
    });
  }

  return filtered;
};

function clearCustom(state, value, index) {
  let newValue = '';
  if (value == false) {
    state.fasaraSheets[index].custom = newValue;
  }
}

const autoCompleteProps = {
  clearOnBlur: true,
  freeSolo: true,
  filterOptions,
  getOptionLabel: (option) =>
    option.useValue ? option.value.name : option.label || '',
  renderOption: (option) => option.label,
  parseNewValue: (value) => ({
    value,
    label: value.name,
  }),
};

function authHeaders() {
  return {
    Authorization: `Bearer ${window.localStorage.getItem(ACCESS_TOKEN)}`,
  };
}

function normalizeFamiliesPayload(data) {
  const list = Array.isArray(data) ? data : data?.data;
  return Array.isArray(list) ? list : [];
}

function mapFamilyOption(row) {
  const raw = row.id ?? row.value ?? row.code;
  const value =
    raw === '' || raw === null || raw === undefined ? raw : String(raw);
  return {
    label: row.name ?? row.label ?? row.code ?? '',
    value,
    type: row.type,
  };
}

/**
 * Glass / FASARA™ categories (step 1).
 * Backend: GET /family-categories/FASARA (or GET /family-categories with shared handler)
 */
async function fetchFasaraGlassCategories() {
  const url = `${API_URL}/family-categories/FASARA`;
  try {
    const response = await axios.get(url, {
      headers: authHeaders(),
    });
    return normalizeFamiliesPayload(response.data)
      .map(mapFamilyOption)
      .filter((o) => o.value != null && o.value !== '');
  } catch (err) {
    console.error('fetchFasaraGlassCategories', err);
    return [];
  }
}

/**
 * Families under a selected category (step 2).
 * Backend: GET /families/arqfinishes?type=FASARA&category=<value> → CatalogFamilies("FASARA", category)
 */
async function fetchFasaraFamiliesForCategory(categoryValue) {
  const url = `${API_URL}/families/arqfinishes`;
  try {
    const response = await axios.get(url, {
      params: { type: 'FASARA', category: categoryValue },
      headers: authHeaders(),
    });
    return normalizeFamiliesPayload(response.data)
      .map(mapFamilyOption)
      .filter((o) => o.value != null && o.value !== '');
  } catch (err) {
    console.error('fetchFasaraFamiliesForCategory', err);
    return [];
  }
}

function firstNonemptyString(...candidates) {
  for (const v of candidates) {
    if (v != null && String(v).trim() !== '') {
      return v;
    }
  }
  return undefined;
}

/**
 * Same idea as DI-NOC express (`filmOptionsForRow` uses `item.name`): submit `film`
 * as catalog code/slug (e.g. "AE-1633", "SH2APCR"), not numeric id, for `/warranties/arqFinishes`.
 */
function fasaraFilmFormValue(item) {
  if (!item || typeof item !== 'object') {
    return '';
  }
  const byCode =
    item.code ?? item.filmCode ?? item.sku ?? item.productCode ?? null;
  if (byCode != null && String(byCode).trim() !== '') {
    return String(byCode).trim();
  }
  const byName = item.name ?? item.label ?? item.filmName ?? null;
  if (byName != null && String(byName).trim() !== '') {
    return String(byName).trim();
  }
  const fallback = item.value ?? item.id;
  return fallback == null || fallback === '' ? '' : String(fallback).trim();
}

function normalizeFasaraFilmsPayload(data, family) {
  const list = Array.isArray(data) ? data : data?.data;
  if (!Array.isArray(list)) {
    return [];
  }
  return list
    .map((item) => {
      const label = item.label ?? item.name ?? '';
      const itemFamily = item.family ?? family;
      const value = fasaraFilmFormValue(item);
      return {
        ...item,
        label,
        value,
        uuid: item.uuid ?? item.UUID ?? null,
        family: itemFamily,
        externalThumbnailUrl: firstNonemptyString(
          item.externalThumbnailUrl,
          item.external_thumbnail_url,
        ),
        externalImageUrl: firstNonemptyString(
          item.externalImageUrl,
          item.external_image_url,
          item.externalImageURL,
        ),
      };
    })
    .filter((item) => item.value !== '' && item.value != null);
}

/** Selected product row (form `film` matches normalized `value`, or id/code/name/label). */
function findFasaraProductRow(familyProducts, film) {
  if (!Array.isArray(familyProducts) || film == null || film === '') {
    return null;
  }
  return familyProducts.find((p) => {
    const keys = [
      p.uuid,
      p.UUID,
      p.code,
      p.filmCode,
      p.sku,
      p.value,
      p.id,
      p.name,
      p.label,
    ].filter((k) => k != null && k !== '');
    return keys.some((k) => String(k).trim() === String(film).trim());
  });
}

/** Local `/media/fasara/*.jpg`: prefer API code/name from row when `film` is an id. */
function getFasaraLocalFilmPreviewSrc({ film, familyProducts }) {
  const publicBase = process.env.PUBLIC_URL || '';
  if (!film) {
    return `${publicBase}/media/fasara/Choose.jpg`;
  }
  if (film === 'Custom') {
    return `${publicBase}/media/fasara/Custom.jpg`;
  }
  const row = findFasaraProductRow(familyProducts, film);
  const base =
    row &&
    (row.code ??
      row.filmCode ??
      row.sku ??
      (typeof row.value === 'string' ? row.value : null) ??
      (typeof row.name === 'string' ? row.name.trim() || null : null) ??
      (typeof row.label === 'string' ? row.label.trim() || null : null));
  const file =
    base != null && String(base).trim() !== ''
      ? `${String(base).trim()}.jpg`
      : `${String(film)}.jpg`;
  return `${publicBase}/media/fasara/${file}`;
}

function firstFasaraExternalPreviewUrl(match) {
  return firstNonemptyString(
    match?.externalThumbnailUrl,
    match?.external_thumbnail_url,
    match?.externalImageUrl,
    match?.external_image_url,
    match?.externalImageURL,
  );
}

function getFasaraSheetPreviewSrc({ film, familyProducts }) {
  const localSrc = getFasaraLocalFilmPreviewSrc({ film, familyProducts });
  if (!film || film === 'Custom') {
    return localSrc;
  }
  const match = findFasaraProductRow(familyProducts, film);
  return firstFasaraExternalPreviewUrl(match) ?? localSrc;
}

/**
 * Products for the selected family (step 3).
 * Backend: GET /films/arqfinishes?family=<family> → ResolveCatalogTypeByFamily + FilmsByCatalogType
 * Response is usually the whole catalog for that type; we narrow by `family` when rows include it.
 */
async function fetchFasaraProductsByFamily(family) {
  const url = `${API_URL}/films/arqfinishes`;
  try {
    const response = await axios.get(url, {
      params: { family },
      headers: authHeaders(),
    });
    let items = normalizeFasaraFilmsPayload(response.data, family);
    const narrowed = items.filter((item) => {
      if (item.family == null && item.familyId == null && item.line == null) {
        return true;
      }
      return (
        String(item.family) === String(family) ||
        String(item.familyId) === String(family)
      );
    });
    if (narrowed.length > 0) {
      items = narrowed;
    }
    return items.sort((a, b) => String(a.label).localeCompare(String(b.label)));
  } catch (err) {
    console.error('fetchFasaraProductsByFamily', err);
    return [];
  }
}

function filmOptionsForRow(family, loadedProducts) {
  if (!family) {
    return [];
  }
  if (family === 'Custom') {
    return [{ value: 'Custom', label: 'Custom' }];
  }
  if (loadedProducts == null) {
    return [];
  }
  return loadedProducts?.map((item) => ({
    label: item.label,
    value: item.value,
    uuid: item.uuid ?? item.UUID ?? null,
    externalThumbnailUrl: item.externalThumbnailUrl,
    externalImageUrl: item.externalImageUrl,
  }));
}

const fasaraApplicationSheetsMapStateToProps = (state) => ({
  distributorsOptions: getDistributorsOptions(state),
});

const fasaraApplicationSheetsMapDispatchToProps = (dispatch) => ({
  fetchDistributors: () => dispatch(distributorsRequest.fetch()),
});

const FasaraApplicationSheetRow = ({
  index,
  values,
  categoriesOptions,
  categoriesLoading,
  filteredOptions,
  onRemove,
  showRemoveButton,
}) => {
  const { setFieldValue } = useFormikContext();
  const sheet = values.fasaraSheets[index];
  const glassFinish = sheet?.glassFinish;
  const family = sheet?.family;
  const prevCategoryRef = useRef(glassFinish);
  const prevFamilyRef = useRef(family);
  const [familyProducts, setFamilyProducts] = useState(undefined);
  const [familyOptionsList, setFamilyOptionsList] = useState([]);
  const [familiesLoading, setFamiliesLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (!glassFinish) {
      setFamilyOptionsList([]);
      setFamiliesLoading(false);
      return undefined;
    }
    if (glassFinish === 'Custom') {
      setFamilyOptionsList(CUSTOM_FAMILY_OPTIONS);
      setFamiliesLoading(false);
      return undefined;
    }

    setFamiliesLoading(true);
    setFamilyOptionsList([]);
    fetchFasaraFamiliesForCategory(glassFinish).then((opts) => {
      if (!cancelled) {
        setFamilyOptionsList(opts);
        setFamiliesLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [glassFinish]);

  useEffect(() => {
    const prev = prevCategoryRef.current;
    if (prev != null && glassFinish != null && prev !== glassFinish) {
      setFieldValue(`fasaraSheets[${index}].family`, null);
      setFieldValue(`fasaraSheets[${index}].familyLabel`, null);
      setFieldValue(`fasaraSheets[${index}].film`, null);
      setFieldValue(`fasaraSheets[${index}].filmLabel`, null);
      setFieldValue(`fasaraSheets[${index}].filmUuid`, null);
      setFieldValue(`fasaraSheets[${index}].filmData`, null);
      setFieldValue(`fasaraSheets[${index}].custom`, '');
    }
    prevCategoryRef.current = glassFinish;
  }, [glassFinish, index, setFieldValue]);

  useEffect(() => {
    const prev = prevFamilyRef.current;
    if (prev != null && family != null && prev !== family) {
      setFieldValue(`fasaraSheets[${index}].film`, null);
      setFieldValue(`fasaraSheets[${index}].filmLabel`, null);
      setFieldValue(`fasaraSheets[${index}].filmUuid`, null);
      setFieldValue(`fasaraSheets[${index}].filmData`, null);
      setFieldValue(`fasaraSheets[${index}].custom`, '');
    }
    prevFamilyRef.current = family;
  }, [family, index, setFieldValue]);

  useEffect(() => {
    let cancelled = false;

    if (!family || !glassFinish) {
      setFamilyProducts(undefined);
      return undefined;
    }
    if (family === 'Custom') {
      setFamilyProducts(undefined);
      return undefined;
    }
    if (!familyOptionsList.some((o) => String(o.value) === String(family))) {
      setFamilyProducts(undefined);
      return undefined;
    }

    setFamilyProducts(null);
    fetchFasaraProductsByFamily(family).then((items) => {
      if (!cancelled) {
        setFamilyProducts(items);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [glassFinish, family, familyOptionsList]);

  const filmDisabled =
    !glassFinish || !family || (family !== 'Custom' && familyProducts == null);

  const previewSrc = sheet?.filmData
    ? getFasaraViewSheetImgSrc(sheet)
    : getFasaraSheetPreviewSrc({
        film: sheet?.film,
        familyProducts,
      });

  return (
    <div className="row">
      <div className="col-sm-12 col-lg-6">
        <div className="row">
          <div className="col-12">
            <Field
              name={`fasaraSheets[${index}].glassFinish`}
              component={Autocomplete}
              translate="false"
              label="Categoría *"
              variant="outlined"
              disabled={categoriesLoading}
              options={categoriesOptions}
              handleChange={(opt) => {
                if (opt && typeof opt === 'object' && opt.label != null) {
                  setFieldValue(
                    `fasaraSheets[${index}].glassFinishLabel`,
                    String(opt.label),
                  );
                } else {
                  setFieldValue(
                    `fasaraSheets[${index}].glassFinishLabel`,
                    null,
                  );
                }
              }}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <Field
              name={`fasaraSheets[${index}].family`}
              component={Autocomplete}
              label="Familia *"
              variant="outlined"
              translate="false"
              disabled={!glassFinish || categoriesLoading || familiesLoading}
              options={familyOptionsList}
              handleChange={(opt) => {
                if (opt && typeof opt === 'object' && opt.label != null) {
                  setFieldValue(
                    `fasaraSheets[${index}].familyLabel`,
                    String(opt.label),
                  );
                } else {
                  setFieldValue(`fasaraSheets[${index}].familyLabel`, null);
                }
              }}
            />
          </div>
        </div>

        <div className="Row">
          <div className="col-12">
            <Field
              name={`fasaraSheets[${index}].film`}
              component={Autocomplete}
              label="Producto *"
              helperText="Por favor seleccione una película 3M™ Fasara™ "
              variant="outlined"
              translate="false"
              disabled={filmDisabled}
              options={filmOptionsForRow(family, familyProducts)}
              onBlur={clearCustom(
                values,
                values.fasaraSheets[index].film == 'Custom' &&
                  values.fasaraSheets[index].family == 'Custom',
                index,
              )}
              handleChange={(opt) => {
                if (!opt || opt.value === 'Custom' || family === 'Custom') {
                  setFieldValue(`fasaraSheets[${index}].filmData`, null);
                  setFieldValue(`fasaraSheets[${index}].filmUuid`, null);
                  setFieldValue(`fasaraSheets[${index}].filmLabel`, null);
                  return;
                }
                if (opt.label != null && String(opt.label).trim() !== '') {
                  setFieldValue(
                    `fasaraSheets[${index}].filmLabel`,
                    String(opt.label).trim(),
                  );
                }
                const uuid = opt.uuid;
                if (uuid != null && String(uuid).trim() !== '') {
                  setFieldValue(
                    `fasaraSheets[${index}].filmUuid`,
                    String(uuid),
                  );
                  setFieldValue(`fasaraSheets[${index}].filmData`, null);
                  fetchFasaraFilmByUuid(uuid).then((detail) => {
                    setFieldValue(
                      `fasaraSheets[${index}].filmData`,
                      detail || null,
                    );
                  });
                } else {
                  setFieldValue(`fasaraSheets[${index}].filmUuid`, null);
                  const row = findFasaraProductRow(familyProducts, opt.value);
                  setFieldValue(
                    `fasaraSheets[${index}].filmData`,
                    row ? { ...row } : null,
                  );
                }
              }}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <Field
              name={`fasaraSheets[${index}].distributor`}
              component={Autocomplete}
              label="Distribuidor"
              helperText="Por favor selecione el distribuidor"
              variant="outlined"
              options={filteredOptions}
              placeholder="Seleccionar"
              InputLabelProps={{ shrink: true }}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <Field
              name={`fasaraSheets[${index}].meters`}
              component={TextField}
              type="text"
              label="M2 necesarios *"
              variant="outlined"
              fullWidth
            />
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <Field
              name={`fasaraSheets[${index}].run`}
              component={TextField}
              type="text"
              label="Número de RUN:*"
              variant="outlined"
              fullWidth
            />
          </div>
        </div>
      </div>
      <div className="col-sm-12 col-lg-6">
        <FilmCatalogPreview
          src={previewSrc}
          alt={String(sheet?.filmData?.name ?? sheet?.film ?? '')}
        >
          <Field
            name={`fasaraSheets[${index}].custom`}
            component={TextField}
            type="text"
            label="Custom 3M™ Glass Finish™"
            variant="outlined"
            fullWidth
            disabled={
              values.fasaraSheets[index].film == 'Custom' &&
              values.fasaraSheets[index].family == 'Custom'
                ? false
                : true
            }
            helperText='Por favor complete sólo si eligió una lámina "CUSTOM"'
          />
        </FilmCatalogPreview>
      </div>
      {showRemoveButton && (
        <>
          <div className="col-12 kt-space-20" />
          <div className="col-12">
            <Button
              size="small"
              variant="outlined"
              fullWidth
              onClick={onRemove}
            >
              <DeleteIcon color="primary" />
              <FormattedMessage id="Eliminar esta lámina" />
            </Button>
            <div className="col-12 kt-space-12" />
          </div>
        </>
      )}
    </div>
  );
};

const FasaraApplicationSheetsInner = ({
  country,
  values,
  fasaraSheetsError,
  distributorsOptions,
  fetchDistributors,
}) => {
  const [categoriesOptions, setCategoriesOptions] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  useEffect(() => {
    fetchDistributors();
  }, [fetchDistributors]);

  useEffect(() => {
    let cancelled = false;
    fetchFasaraGlassCategories().then((items) => {
      if (!cancelled) {
        setCategoriesOptions([...items, { value: 'Custom', label: 'Custom' }]);
        setCategoriesLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredOptions = distributorsOptions;

  // const distributorFiltered = distributorsOptions.filter((distributor) => {
  //   return distributor.users.some((user) => user.fasara === true);
  // });

  // const filteredOptions = distributorFiltered.filter((option) => {
  //   const isNLorBE = country === 'NL' || country === 'BE';

  //   if (isNLorBE == true) {
  //     return (
  //       !option.label.toLowerCase().includes('3m') &&
  //       !option.label.toLowerCase().includes('test') &&
  //       !option.label.toLowerCase().includes('distributor') &&
  //       !/^[a-z]+distributor/.test(option.label.toLowerCase())
  //     );
  //   } else {
  //     return (
  //       !option.label.toLowerCase().includes('test') &&
  //       !option.label.toLowerCase().includes('distributor') &&
  //       !/^[a-z]+distributor/.test(option.label.toLowerCase())
  //     );
  //   }
  // });

  return (
    <FieldArray name="fasaraSheets">
      {({ push, remove }) => (
        <>
          {values.fasaraSheets.length < 1 ? push(newSheet) : <></>}
          {values.fasaraSheets.map((_, index) => (
            <FasaraApplicationSheetRow
              key={index}
              index={index}
              values={values}
              categoriesOptions={categoriesOptions}
              categoriesLoading={categoriesLoading}
              filteredOptions={filteredOptions}
              onRemove={() => remove(index)}
              showRemoveButton={values.fasaraSheets.length > 0}
            />
          ))}
          {values.fasaraSheets.length < 6 && (
            <>
              <div className="kt-space-20" />

              <div className="row">
                <div className="col-12">
                  <Button
                    size="small"
                    variant="outlined"
                    fullWidth
                    onClick={() => push(newSheet)}
                  >
                    <FormattedMessage id="Agregar nueva lámina" />
                  </Button>
                </div>
              </div>
            </>
          )}
          {typeof fasaraSheetsError === 'string' && (
            <div className="row">
              <div className="col-sm-12">
                <FormHelperText error={Boolean(fasaraSheetsError)}>
                  {fasaraSheetsError}
                </FormHelperText>
              </div>
            </div>
          )}
          <div className="kt-space-20" />
        </>
      )}
    </FieldArray>
  );
};

const FasaraApplicationSheets = connect(
  fasaraApplicationSheetsMapStateToProps,
  fasaraApplicationSheetsMapDispatchToProps,
)(FasaraApplicationSheetsInner);

export const Step3 = ({
  country,
  values,
  errors: { fasaraSheets },
  prevStep,
  fetchUserInstallersRequest,
  userInstallersOptions,
}) => {
  useEffect(() => {
    fetchUserInstallersRequest();
  }, [fetchUserInstallersRequest]);

  return (
    <>
      <div className="kt-space-20" />

      <div className="row">
        <div className="col-sm-12 col-md-12 col-lg-2" />
        <div className="col-sm-12 col-md-12 col-lg-8">
          <div className="row">
            <div className="col-12">
              <PorletHeader>
                <PorletHeaderTitle variant="h6">
                  <FormattedMessage id="Colocación" />
                </PorletHeaderTitle>
              </PorletHeader>
            </div>
          </div>
          <div className="row">
            <div className="col-sm-12 col-lg-6">
              <div className="col-12">
                <FieldArray name="installers">
                  {({ push, remove }) => (
                    <>
                      {values.installers.map((_, index) => (
                        <div className="row" key={index}>
                          <div className="col-12">
                            <Field
                              name={`installers[${index}]`}
                              component={Autocomplete}
                              emptyValue={newInstaller}
                              autoCompleteProps={autoCompleteProps}
                              label="Instalador *"
                              helperText="Por favor seleccione la persona que realizó la colocación"
                              variant="outlined"
                              options={userInstallersOptions}
                              placeholder="Seleccionar"
                              InputLabelProps={{ shrink: true }}
                            />
                          </div>
                          {values.installers.length > 1 && (
                            <>
                              <div className="col-12 kt-space-20" />
                              <div className="col-12">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  fullWidth
                                  onClick={() => remove(index)}
                                >
                                  <DeleteIcon color="primary" />
                                  <FormattedMessage id="Eliminar este instalador" />
                                </Button>
                                <div className="col-12 kt-space-12" />
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                      {values.installers.length <= 4 && (
                        <>
                          <div className="kt-space-20" />

                          <div className="row">
                            <div className="col-12">
                              <Button
                                size="small"
                                variant="outlined"
                                fullWidth
                                onClick={() => push(newInstaller)}
                              >
                                <FormattedMessage id="Agregar nuevo instalador" />
                              </Button>
                            </div>
                          </div>
                        </>
                      )}
                      <div className="kt-space-20" />
                    </>
                  )}
                </FieldArray>
              </div>
            </div>
            <div className="col-sm-12 col-lg-6">
              <div className="col-12">
                <Field
                  component={DatePicker}
                  fullWidth
                  name="installDate"
                  label={<FormattedMessage id="Fecha de instalación" />}
                  inputVariant="outlined"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="col-sm-12 col-md-12 col-lg-2" />
      </div>
      <div className="row">
        <div className="col-sm-12 col-md-12 col-lg-2" />
        <div className="col-sm-12 col-md-12 col-lg-8">
          <div className="row">
            <div className="col-sm-12 col-md-12 col-lg-12">
              <div className="row">
                <div className="col-12">
                  <div className="kt-space-20" />
                  <PorletHeader>
                    <PorletHeaderTitle variant="h6">
                      <FormattedMessage id="3M™ Glass Finishes" />
                    </PorletHeaderTitle>
                  </PorletHeader>
                </div>
              </div>
              <div className="kt-space-20" />
              <FasaraApplicationSheets
                country={country}
                values={values}
                fasaraSheetsError={fasaraSheets}
              />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-12 col-lg-2" />
      </div>
      <div className="row">
        <div className="col-sm-12 col-md-12 col-lg-2" />
        <div className="col-sm-12 col-md-12 col-lg-8 submit-bottons">
          <Button variant="contained" type="submit" color="primary">
            <FormattedMessage id="Siguiente" />
          </Button>
          <Button variant="outlined" onClick={prevStep}>
            <FormattedMessage id="Atrás" />
          </Button>
        </div>
        <div className="col-sm-12 col-md-12 col-lg-2" />
      </div>
    </>
  );
};

export const mapStateToProps = (state, { values: { installers } }) => ({
  userInstallersOptions: getUserInstallersOptions(state).filter(
    (item) => !installers.find((installer) => installer.id === item.value.id),
  ),
});

export const mapDispatchToProps = (dispatch) => ({
  fetchUserInstallersRequest: () => dispatch(userInstallersRequest.fetch()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Step3);
